import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useLicense } from "../../../context/licenseContext";
import { useToast } from "../../../context/toastContext";
import apiClient from "../../../api/apiClient";
import { formatWeightFromGrams } from "../../../utils/weightUtils";
import OrderStatusBadge from "./OrderStatusBadge";
import OrderTimeline from "./OrderTimeline";
import InvoiceSection from "./InvoiceSection";
import ShippingSection from "./ShippingSection";
import ShippingLabelDownload from "../../../components/ShippingLabelDownload/ShippingLabelDownload";
import ReceiptSection from "./ReceiptSection";
import OrderActions from "./OrderActions";
import SendInvoiceModal from "../../../components/SendInvoiceModal/SendInvoiceModal";
import ShipOrderModal from "../../../components/ShipOrderModal/ShipOrderModal";
import ReceiptUpload from "../../../components/ReceiptUpload/ReceiptUpload";
import PickupSection from "./PickupSection";
import ChainOfCustodySection from "./ChainOfCustodySection";
import "./OrderDetailDrawer.css";

const OrderDetailDrawer = ({ order, onClose, onOrderUpdated }) => {
    const { currentLicense } = useLicense();
    const toast = useToast();

    const [detail, setDetail] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null); 
    const [actionLoading, setActionLoading] = useState(false);

    // Modal visibility
    const [showInvoiceModal, setShowInvoiceModal] = useState(false);
    const [showShipModal, setShowShipModal] = useState(false);

    const fetchDetail = useCallback(async () => {
        if (!order?.id || !currentLicense?.licenseNo) return;

        setIsLoading(true);
        setError(null);

        try {
            const res = await apiClient.get(
                `/orders/${order.id}?licenseNo=${encodeURIComponent(currentLicense.licenseNo)}`
            );
            setDetail(res.data);
        } catch (err) {
            console.error("[OrderDetailDrawer] fetch error:", err);
            setError(
                err.response?.data?.error ||
                err.userMessage ||
                "Failed to load order details."
            );
        } finally {
            setIsLoading(false);
        }
    }, [order?.id, currentLicense?.licenseNo]);

    useEffect(() => {
        fetchDetail();
    }, [fetchDetail]);

    // Lock body scroll while drawer is open
    useEffect(() => {
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = "";
        };
    }, []);

    // Close on Escape
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape" && !showInvoiceModal && !showShipModal) {
                onClose();
            }
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onClose, showInvoiceModal, showShipModal]);

    const fullOrder = useMemo(() => {
        const loadedOrder = detail?.order || order;
        if (!loadedOrder) return loadedOrder;
        return {
            ...loadedOrder,
            user_role: loadedOrder.seller_license_no === currentLicense?.licenseNo ? "seller" : "buyer",
        };
    }, [detail?.order, order, currentLicense?.licenseNo]);
    const invoice = detail?.invoice || null;
    const photos = detail?.receipt_photos || [];
    const handoffPhotos = detail?.handoff_photos || [];
    const timeline = detail?.timeline || [];

    const isBuyer = fullOrder?.user_role === "buyer";
    const isLocalHandoff = ["buyer_pickup", "seller_delivery"].includes(fullOrder?.fulfillment_method);
    const canUploadReceipt =
        isBuyer &&
        (fullOrder?.status === "shipped" ||
            (isLocalHandoff && ["paid", "handoff_partially_confirmed"].includes(fullOrder?.status)));

    const hasModalOpen = showInvoiceModal || showShipModal;

    // ── Action handlers ──

    const handleAction = useCallback(
        async (actionType, data) => {
            switch (actionType) {
                case "send-invoice":
                    setShowInvoiceModal(true);
                    return;

                case "ship":
                    setShowShipModal(true);
                    return;

                case "receive":
                    // Scroll to receipt upload section if visible, otherwise handled by ReceiptUpload
                    const receiptEl = document.querySelector(".receipt-section");
                    if (receiptEl) {
                        receiptEl.scrollIntoView({ behavior: "smooth", block: "center" });
                    }
                    return;

                case "confirm-payment":
                    setActionLoading(true);
                    try {
                        await apiClient.patch(`/orders/${fullOrder.id}/confirm-payment`, {
                            licenseNo: currentLicense.licenseNo,
                        });
                        toast.success("Payment confirmed");
                        await fetchDetail();
                        if (onOrderUpdated) onOrderUpdated();
                    } catch (err) {
                        toast.error(
                            err.response?.data?.error || "Failed to confirm payment."
                        );
                    } finally {
                        setActionLoading(false);
                    }
                    return;

                case "notify-payment":
                    setActionLoading(true);
                    try {
                        await apiClient.patch(`/orders/${fullOrder.id}/notify-payment`, {
                            licenseNo: currentLicense.licenseNo,
                        });
                        toast.success("Payment notification sent to seller");
                        await fetchDetail();
                    } catch (err) {
                        toast.error(
                            err.response?.data?.error || "Failed to send payment notification."
                        );
                    } finally {
                        setActionLoading(false);
                    }
                    return;

                case "deny":
                    setActionLoading(true);
                    try {
                        await apiClient.patch(`/orders/${fullOrder.id}/deny`, {
                            licenseNo: currentLicense.licenseNo,
                        });
                        toast.success("Order denied");
                        if (onOrderUpdated) onOrderUpdated();
                    } catch (err) {
                        toast.error(
                            err.response?.data?.error || "Failed to deny order."
                        );
                    } finally {
                        setActionLoading(false);
                    }
                    return;

                case "cancel":
                    setActionLoading(true);
                    try {
                        await apiClient.patch(`/orders/${fullOrder.id}/cancel`, {
                            licenseNo: currentLicense.licenseNo,
                        });
                        toast.success("Order cancelled");
                        if (onOrderUpdated) onOrderUpdated();
                    } catch (err) {
                        toast.error(
                            err.response?.data?.error || "Failed to cancel order."
                        );
                    } finally {
                        setActionLoading(false);
                    }
                    return;

                default:
                    console.warn("[OrderDetailDrawer] unknown action:", actionType);
            }
        },
        [fullOrder, currentLicense, toast, fetchDetail, onOrderUpdated]
    );

    const handleSendInvoice = useCallback(
        async (payload) => {
            setActionLoading(true);
            try {
                await apiClient.patch(`/orders/${fullOrder.id}/send-invoice`, {
                    licenseNo: currentLicense.licenseNo,
                    ...payload,
                });
                toast.success("Invoice sent successfully");
                setShowInvoiceModal(false);
                await fetchDetail();
                if (onOrderUpdated) onOrderUpdated();
            } catch (err) {
                toast.error(
                    err.response?.data?.error || "Failed to send invoice."
                );
            } finally {
                setActionLoading(false);
            }
        },
        [fullOrder, currentLicense, toast, fetchDetail, onOrderUpdated]
    );

    const handleShip = useCallback(
        async (payload) => {
            setActionLoading(true);
            try {
                await apiClient.patch(`/orders/${fullOrder.id}/ship`, {
                    licenseNo: currentLicense.licenseNo,
                    ...payload,
                });
                toast.success(
                    payload.fulfillment_method === "carrier_shipping"
                        ? "Order marked as shipped"
                        : "Fulfillment method confirmed"
                );
                setShowShipModal(false);
                await fetchDetail();
                if (onOrderUpdated) onOrderUpdated();
            } catch (err) {
                toast.error(
                    err.response?.data?.error || "Failed to ship order."
                );
            } finally {
                setActionLoading(false);
            }
        },
        [fullOrder, currentLicense, toast, fetchDetail, onOrderUpdated]
    );

    const handleReceiptComplete = useCallback(async () => {
        await fetchDetail();
        if (onOrderUpdated) onOrderUpdated();
    }, [fetchDetail, onOrderUpdated]);

    return (
        <>
            <div
                className={`order-drawer-overlay${hasModalOpen ? " order-drawer-overlay--blocked" : ""}`}
                onClick={(e) => {
                    if (hasModalOpen) return;
                    if (e.target === e.currentTarget) onClose();
                }}
            />
            <div
                className="order-drawer"
                role="dialog"
                aria-modal="true"
                aria-labelledby="order-drawer-title"
            >
                {/* Header */}
                <div className="order-drawer__header">
                    <div className="order-drawer__header-content">
                        <h2 className="order-drawer__title" id="order-drawer-title">
                            {fullOrder?.product_name || "Order Details"}
                        </h2>
                        <div className="order-drawer__header-meta">
                            <OrderStatusBadge status={fullOrder?.status} />
                            {fullOrder?.weight_requested_grams && (
                                <span className="order-drawer__weight">
                                    {formatWeightFromGrams(fullOrder.weight_requested_grams)}
                                </span>
                            )}
                        </div>
                    </div>
                    <button
                        className="order-drawer__close"
                        onClick={onClose}
                        aria-label="Close order details"
                        type="button"
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>

                {/* Body */}
                <div className="order-drawer__body">
                    {isLoading && (
                        <div className="order-drawer__loading">
                            <div className="order-drawer__spinner" />
                            <span>Loading order details...</span>
                        </div>
                    )}

                    {!isLoading && error && (
                        <div className="order-drawer__error">
                            <p>{error}</p>
                            <button
                                className="order-drawer__retry"
                                onClick={fetchDetail}
                                type="button"
                            >
                                Retry
                            </button>
                        </div>
                    )}

                    {!isLoading && !error && detail && (
                        <>
                            {/* Order info summary */}
                            <div className="order-drawer__section order-drawer__info">
                                <div className="order-drawer__info-row">
                                    <span className="order-drawer__info-label">Order ID</span>
                                    <span className="order-drawer__info-value">#{fullOrder.id}</span>
                                </div>
                                {fullOrder.user_role && (
                                    <div className="order-drawer__info-row">
                                        <span className="order-drawer__info-label">Your Role</span>
                                        <span className={`order-drawer__info-value order-drawer__role-chip order-drawer__role-chip--${fullOrder.user_role}`}>
                                            {fullOrder.user_role === "seller" ? "Seller" : "Buyer"}
                                        </span>
                                    </div>
                                )}
                                {fullOrder.seller_company && (
                                    <div className="order-drawer__info-row">
                                        <span className="order-drawer__info-label">Seller</span>
                                        <span className="order-drawer__info-value">
                                            <span className="order-drawer__info-company">{fullOrder.seller_company}</span>
                                            {fullOrder.seller_contact_email && (
                                                <span className="order-drawer__info-email">{fullOrder.seller_contact_email}</span>
                                            )}
                                        </span>
                                    </div>
                                )}
                                {fullOrder.buyer_company && (
                                    <div className="order-drawer__info-row">
                                        <span className="order-drawer__info-label">Buyer</span>
                                        <span className="order-drawer__info-value">
                                            <span className="order-drawer__info-company">{fullOrder.buyer_company}</span>
                                            {fullOrder.requester_email && (
                                                <span className="order-drawer__info-email">{fullOrder.requester_email}</span>
                                            )}
                                        </span>
                                    </div>
                                )}
                                {fullOrder.created_at && (
                                    <div className="order-drawer__info-row">
                                        <span className="order-drawer__info-label">Requested</span>
                                        <span className="order-drawer__info-value">
                                            {new Date(fullOrder.created_at).toLocaleDateString("en-US", {
                                                month: "short",
                                                day: "numeric",
                                                year: "numeric",
                                            })}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* Timeline */}
                            {timeline.length > 0 && (
                                <div className="order-drawer__section">
                                    <OrderTimeline timeline={timeline} />
                                </div>
                            )}

                            {/* Invoice */}
                            {invoice && (
                                <div className="order-drawer__section">
                                    <InvoiceSection
                                        invoice={invoice}
                                        lineItems={invoice.line_items}
                                    />
                                </div>
                            )}

                            {/* Shipping */}
                            {["paid", "shipped", "received", "completed"].includes(fullOrder.status) &&
                                (!fullOrder.fulfillment_method || ["carrier_shipping", "seller_delivery"].includes(fullOrder.fulfillment_method)) && (
                                <div className="order-drawer__section">
                                    <ShippingLabelDownload order={fullOrder} />
                                </div>
                            )}
                            {(fullOrder.tracking_number || fullOrder.shipped_at) && (
                                <div className="order-drawer__section">
                                    <ShippingSection order={fullOrder} />
                                </div>
                            )}

                            {["buyer_pickup", "seller_delivery"].includes(fullOrder.fulfillment_method) && (
                                <div className="order-drawer__section">
                                    <PickupSection order={fullOrder} photos={handoffPhotos} />
                                </div>
                            )}

                            {/* Chain of Custody form download */}
                            {fullOrder.coc_pdf_generated_at && (
                                <div className="order-drawer__section">
                                    <ChainOfCustodySection order={fullOrder} />
                                </div>
                            )}

                            {/* Receipt photos */}
                            {(photos.length > 0 || canUploadReceipt) && (
                                <div className="order-drawer__section">
                                    <ReceiptSection
                                        order={fullOrder}
                                        photos={photos}
                                        canUpload={canUploadReceipt}
                                        onUploadComplete={handleReceiptComplete}
                                    >
                                        {canUploadReceipt && (
                                            <ReceiptUpload
                                                orderId={fullOrder.id}
                                                licenseNo={currentLicense.licenseNo}
                                                onUploadComplete={handleReceiptComplete}
                                            />
                                        )}
                                    </ReceiptSection>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Actions Footer */}
                {!isLoading && !error && detail && (
                    <div className="order-drawer__footer">
                        <OrderActions
                            order={fullOrder}
                            onAction={handleAction}
                            loading={actionLoading}
                        />
                    </div>
                )}
            </div>

            {/* Modals — rendered in elevated layer above drawer */}
            {hasModalOpen && (
                <div className="order-drawer-modal-layer">
                    {showInvoiceModal && (
                        <SendInvoiceModal
                            order={fullOrder}
                            onSend={handleSendInvoice}
                            onCancel={() => setShowInvoiceModal(false)}
                        />
                    )}

                    {showShipModal && (
                        <ShipOrderModal
                            order={fullOrder}
                            onShip={handleShip}
                            onCancel={() => setShowShipModal(false)}
                        />
                    )}
                </div>
            )}
        </>
    );
};

export default OrderDetailDrawer;
